import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  TableRestaurant,
  TakeoutDining,
  Person,
  Phone,
  LocationOn,
  EventSeat,
  StickyNote2,
} from "@mui/icons-material";

// Reusable info card for each field
const InfoCard = ({ icon, iconColor = "#667eea", label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.5,
        borderRadius: "12px",
        bgcolor: alpha(iconColor, 0.06),
        border: `1px solid ${alpha(iconColor, 0.12)}`,
        height: "100%",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: alpha(iconColor, 0.15),
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20, color: iconColor } })}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: alpha(theme.palette.text.secondary, 0.75),
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.65rem",
            letterSpacing: "0.6px",
            display: "block",
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
};

const OrderInfoSection = ({ orderDetails }) => {
  const theme = useTheme();
  const isDineIn = orderDetails.orderType === "dine-in";

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: "white",
        borderRadius: "16px",
        p: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 2px 10px ${alpha("#000", 0.05)}`,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            background: `linear-gradient(135deg, #667eea, #764ba2)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `0 4px 12px ${alpha("#667eea", 0.3)}`,
            flexShrink: 0,
          }}
        >
          {isDineIn ? (
            <TableRestaurant sx={{ fontSize: 20, color: "white" }} />
          ) : (
            <TakeoutDining sx={{ fontSize: 20, color: "white" }} />
          )}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            fontSize: "1rem",
          }}
        >
          Order Information
        </Typography>
      </Box>

      <Divider
        sx={{ mb: 2.5, borderColor: alpha(theme.palette.divider, 0.08) }}
      />

      <Grid container spacing={1.5}>
        {/* Service Type */}
        <Grid size={{ xs: 6 }}>
          <InfoCard
            icon={isDineIn ? <TableRestaurant /> : <TakeoutDining />}
            label="Service Type"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: "0.95rem",
              }}
            >
              {isDineIn ? "Dine In" : "Takeaway"}
            </Typography>
          </InfoCard>
        </Grid>

        {/* Table Number */}
        {isDineIn &&
          (orderDetails.Table?.tableNumber || orderDetails.tableNumber) && (
            <Grid size={{ xs: 6 }}>
              <InfoCard icon={<TableRestaurant />} label="Table Number">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    fontSize: "0.95rem",
                  }}
                >
                  Table{" "}
                  {orderDetails.Table?.tableNumber || orderDetails.tableNumber}
                </Typography>
              </InfoCard>
            </Grid>
          )}

        {/* Location */}
        {orderDetails.Table?.location && (
          <Grid size={{ xs: 6 }}>
            <InfoCard icon={<LocationOn />} label="Location">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: "0.95rem",
                }}
              >
                {orderDetails.Table.location}
              </Typography>
            </InfoCard>
          </Grid>
        )}

        {/* Capacity */}
        {orderDetails.Table?.capacity && (
          <Grid size={{ xs: 6 }}>
            <InfoCard icon={<EventSeat />} label="Capacity">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: "0.95rem",
                }}
              >
                {orderDetails.Table.capacity} seats
              </Typography>
            </InfoCard>
          </Grid>
        )}

        {/* Customer Info */}
        {orderDetails.customerName && (
          <Grid size={{ xs: 12 }}>
            <InfoCard icon={<Person />} iconColor="#10b981" label="Customer">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: "0.95rem",
                  lineHeight: 1.4,
                }}
              >
                {orderDetails.customerName}
              </Typography>
              {orderDetails.customerMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <Phone sx={{ fontSize: 13, color: "#10b981" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.82rem",
                      fontWeight: 600,
                    }}
                  >
                    {orderDetails.customerMobile}
                  </Typography>
                </Box>
              )}
            </InfoCard>
          </Grid>
        )}

        {/* Special Instructions */}
        {orderDetails.specialInstructions && (
          <Grid size={{ xs: 12 }}>
            <InfoCard
              icon={<StickyNote2 />}
              iconColor="#f59e0b"
              label="Special Instructions"
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: "0.95rem",
                  lineHeight: 1.6,
                  fontStyle: "italic",
                }}
              >
                {orderDetails.specialInstructions}
              </Typography>
            </InfoCard>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default OrderInfoSection;
